import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Edit, Trash2, ArrowLeft } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    image_url: "",
    category: "event",
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingEvent) {
        await axios.put(`${API}/events/${editingEvent.id}`, formData);
        toast.success("Event updated successfully!");
      } else {
        await axios.post(`${API}/events`, formData);
        toast.success("Event created successfully!");
      }
      setFormData({ title: "", description: "", date: "", image_url: "", category: "event" });
      setShowForm(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error("Failed to save event");
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: event.date.split('T')[0],
      image_url: event.image_url || "",
      category: event.category || "event",
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await axios.delete(`${API}/events/${id}`);
      toast.success("Event deleted successfully!");
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({ title: "", description: "", date: "", image_url: "", category: "event" });
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#1C221F]">
              Admin Panel
            </h1>
            <p className="text-[#515E58] mt-2">Manage events and news</p>
          </div>
          <Link
            to="/"
            data-testid="admin-back-btn"
            className="bg-transparent border border-[#2B5C45] text-[#2B5C45] hover:bg-[#F9F8F6] rounded-full px-6 py-3 font-medium transition-all inline-flex items-center"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Site
          </Link>
        </div>

        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="admin-add-event-btn"
            className="mb-8 bg-[#2B5C45] text-white hover:bg-[#214835] rounded-full px-6 py-3 font-medium transition-all shadow-sm inline-flex items-center"
          >
            <Plus className="mr-2 h-5 w-5" />
            Add New Event
          </button>
        )}

        {showForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl p-8 shadow-sm border border-[#E4E2DC]/40 mb-8"
          >
            <h2 className="text-2xl font-heading font-medium text-[#1C221F] mb-6">
              {editingEvent ? "Edit Event" : "Add New Event"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#1C221F] mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  required
                  value={formData.title}
                  onChange={handleChange}
                  data-testid="admin-event-title-input"
                  className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1C221F] mb-2">
                  Description *
                </label>
                <textarea
                  name="description"
                  required
                  rows={4}
                  value={formData.description}
                  onChange={handleChange}
                  data-testid="admin-event-description-input"
                  className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#1C221F] mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    required
                    value={formData.date}
                    onChange={handleChange}
                    data-testid="admin-event-date-input"
                    className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#1C221F] mb-2">
                    Category
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    data-testid="admin-event-category-input"
                    className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all"
                  >
                    <option value="event">Event</option>
                    <option value="news">News</option>
                    <option value="workshop">Workshop</option>
                    <option value="achievement">Achievement</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#1C221F] mb-2">
                  Image URL
                </label>
                <input
                  type="url"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  data-testid="admin-event-image-input"
                  placeholder="https://example.com/image.jpg"
                  className="w-full px-4 py-3 rounded-lg border border-[#E4E2DC] focus:border-[#8C9A74] focus:ring-2 focus:ring-[#8C9A74]/20 outline-none transition-all"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  data-testid="admin-event-submit-btn"
                  className="bg-[#2B5C45] text-white hover:bg-[#214835] rounded-full px-8 py-3 font-medium transition-all shadow-sm"
                >
                  {editingEvent ? "Update Event" : "Create Event"}
                </button>
                <button
                  type="button"
                  onClick={cancelForm}
                  data-testid="admin-event-cancel-btn"
                  className="bg-transparent border border-[#515E58] text-[#515E58] hover:bg-[#F9F8F6] rounded-full px-8 py-3 font-medium transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#2B5C45] border-r-transparent"></div>
            <p className="mt-4 text-[#515E58]">Loading events...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {events.map((event) => (
              <div
                key={event.id}
                data-testid={`admin-event-${event.id}`}
                className="bg-white rounded-xl p-6 shadow-sm border border-[#E4E2DC]/40 flex items-start justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-heading font-medium text-[#1C221F]">
                      {event.title}
                    </h3>
                    <span className="inline-block px-3 py-1 bg-[#2B5C45]/10 text-[#2B5C45] text-xs font-medium rounded-full">
                      {event.category}
                    </span>
                  </div>
                  <p className="text-sm text-[#515E58] mb-2">{event.description}</p>
                  <p className="text-xs text-[#8C9A74]">
                    {new Date(event.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(event)}
                    data-testid={`admin-edit-${event.id}`}
                    className="p-2 text-[#2B5C45] hover:bg-[#2B5C45]/10 rounded-lg transition-colors"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(event.id)}
                    data-testid={`admin-delete-${event.id}`}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
            {events.length === 0 && (
              <div className="text-center py-20">
                <p className="text-lg text-[#515E58]">No events yet. Create your first event!</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
